import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/Card';
import CustomerHistoryModal from '../components/CustomerHistoryModal';
import { getCustomerStatus } from '../services/dashboardApi';

// ACTIVE-PAGE v1: Ref. Party-wise active customers; click the number -> those customers; click a customer -> history
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const TH = { textAlign: 'left', verticalAlign: 'bottom', padding: '8px 10px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' };
const TD = { padding: '8px 10px', fontSize: 'var(--font-size-sm)', borderBottom: '1px solid var(--color-border)', verticalAlign: 'middle' };
const FIELD = { padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)', background: 'var(--color-surface)', color: 'var(--color-text-primary)' };
const HINT = { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 };

export default function ActiveCustomersPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState(null); // a Ref. Party id (string) or 'all'
  const [q, setQ] = useState('');
  const [hist, setHist] = useState(null);

  useEffect(() => {
    let alive = true;
    getCustomerStatus()
      .then((s) => {
        if (!alive) return;
        setStatus(s);
        if (s.parties && s.parties.length === 1) setSel(String(s.parties[0].id));
      })
      .catch(() => { if (alive) setErr('Could not load.'); });
    return () => { alive = false; };
  }, []);

  const parties = status && status.parties ? status.parties : [];
  const pick = (k) => { setSel(sel === k ? null : k); setQ(''); };
  const selName = sel === 'all' ? 'All Ref. Parties' : ((parties.find((p) => String(p.id) === sel) || {}).name || '');
  const list = useMemo(() => {
    if (!status || sel === null) return [];
    const src = sel === 'all' ? status.active : ((parties.find((p) => String(p.id) === sel) || {}).active || []);
    const s = q.trim().toLowerCase();
    return src.filter((c) => !s || String(c.name).toLowerCase().includes(s));
  }, [status, parties, sel, q]);

  const numBtn = (label, k, bold) => (
    <button
      type="button"
      onClick={() => pick(k)}
      title="Click to see these customers"
      style={{ background: sel === k ? 'var(--color-accent-soft)' : 'transparent', border: '1px solid var(--color-border)', borderRadius: 999, padding: '2px 14px', cursor: 'pointer', font: 'inherit', fontWeight: bold ? 800 : 700, color: 'var(--color-accent)' }}
    >
      {label}
    </button>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        <h1 style={{ margin: 0 }}>Active Customers{status ? ` — ${MONTHS[status.month - 1]} ${status.year}` : ''}</h1>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>Back to Dashboard</button>
      </div>

      {err && <Card><p style={{ margin: 0, color: 'var(--color-danger)' }}>{err}</p></Card>}
      {!status && !err && <Card><p style={{ margin: 0, color: 'var(--color-text-muted)' }}>Loading…</p></Card>}

      {status && (
        <>
          <Card title="Ref. Party-wise active customers" style={{ marginBottom: 'var(--space-4)' }}>
            {parties.length === 0 ? <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>No active customers.</p> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={TH}>#</th><th style={TH}>Ref. Party</th>
                      <th style={{ ...TH, textAlign: 'right' }}>Active customers</th>
                      <th style={{ ...TH, textAlign: 'right' }}>of which new</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parties.map((p, i) => (
                      <tr key={p.id} style={{ background: sel === String(p.id) ? 'var(--color-accent-soft)' : undefined }}>
                        <td style={TD}>{i + 1}</td>
                        <td style={{ ...TD, fontWeight: 600 }}>{p.name}</td>
                        <td style={{ ...TD, textAlign: 'right' }}>{numBtn(fmt(p.active.length), String(p.id))}</td>
                        <td style={{ ...TD, textAlign: 'right' }}>{fmt(p.newCount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  {parties.length > 1 && (
                    <tfoot>
                      <tr>
                        <td style={{ ...TD, fontWeight: 800, borderTop: '2px solid var(--color-border)' }} colSpan={2}>All Ref. Parties (each customer counted once)</td>
                        <td style={{ ...TD, textAlign: 'right', borderTop: '2px solid var(--color-border)' }}>{numBtn(fmt(status.active.length), 'all', true)}</td>
                        <td style={{ ...TD, borderTop: '2px solid var(--color-border)' }} />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
            <div style={HINT}>Active = dispatched in the last 90 days. Click a number to see those customers.</div>
          </Card>

          {sel !== null && (
            <Card title={`${selName} — ${fmt(list.length)} active customers`} style={{ marginBottom: 'var(--space-4)' }}>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer" style={{ ...FIELD, width: '100%', maxWidth: 320, marginBottom: 8 }} />
              <div style={{ overflowX: 'auto', maxHeight: 520, overflowY: 'auto' }} className="table-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={TH}>#</th><th style={TH}>Customer</th></tr></thead>
                  <tbody>
                    {list.length === 0 && <tr><td style={TD} colSpan={2}>No customer found.</td></tr>}
                    {list.map((c, i) => (
                      <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setHist({ id: c.id, name: c.name })} title="Click to see this customer's dispatch history">
                        <td style={TD}>{i + 1}</td>
                        <td style={{ ...TD, fontWeight: 600 }}>{c.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={HINT}>Click a customer to see that customer&apos;s dispatch history.</div>
            </Card>
          )}
        </>
      )}
      {hist && <CustomerHistoryModal customer={hist} onClose={() => setHist(null)} />}
    </div>
  );
}
